import { useFormik } from 'formik'
import { FC, useCallback, useEffect, useMemo } from 'react'
import {
  applyApiFieldErrors,
  AtendimentoType,
  ButtonType,
  ContratoType,
  Enum,
  GenderType,
  IconPaths,
  IconSize,
  MaskType,
  normalizeNumber,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  ButtonIcon,
  CustomAccordion,
  FetchState,
  FormDateInput,
  FormInput,
  FormRadio,
  FormSelect,
  FormTextArea,
  FormToggle,
  InfoAlert,
  IOption,
  Label,
  ListLoading,
  LoadingIndicator,
  LogoInput,
  useModal,
} from '../../../../../../components'
import {
  AvatarDto,
  ContatoList,
  EmpresaDtoCreate,
  EmpresaDtoUpdate,
  EmpresaView,
  ResponsavelTecnicoDto,
} from '../../../empresas-list/core/_models'
import {
  createEmpresa,
  createSubEmpresa,
  updateEmpresa,
} from '../../../empresas-list/core/_requests'
import { getUserByCPF } from '../../../../../auth/core/_requests'
import { createEmpresaSchema, EmpresaSchemaMode } from '../modal/validation/empresaSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import { criarEmpresaDtoHilab, criarHilabFormValues } from './hilabConfig'

type IProps = {
  mode: EmpresaSchemaMode
  empresaMaeObrigatoria?: boolean
}

export const EmpresaForm: FC<IProps> = ({ mode, empresaMaeObrigatoria = false }) => {
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: empresa, isLoading: isEmpresaLoading } = (fetchStates['empresa'] ||
    {}) as FetchState<EmpresaView>

  const { data: logo } = (fetchStates['logo'] || {}) as FetchState<string>
  const { data: contato } = (fetchStates['contato'] || {}) as FetchState<ContatoList>
  const { data: tiposAtendimento } = (fetchStates['tiposAtendimento'] || {}) as FetchState<Enum[]>

  const possuiResponsavelProprio = !!empresa?.id && !!empresa?.responsavelTecnico

  const convertToAvatarDto = (input: string, action: 'u' | 'd'): AvatarDto => {
    const match = input.match(/^data:(.*?);base64,(.*)$/)

    const fileType = match ? match[1] : ''
    const fileContent = match ? match[2] : ''

    return {
      action,
      fileContent,
      fileType,
    }
  }

  const getTipoAtendimentoOption = useCallback(
    (value: AtendimentoType): IOption<AtendimentoType> | undefined => {
      const tipo = tiposAtendimento?.find((item) => item.name === value)
      return {
        label: tipo?.descricao || value,
        value,
      }
    },
    [tiposAtendimento]
  )

  const rt = empresa?.responsavelTecnico
  const empresaForEdit = useMemo(
    () => ({
      ...empresa,
      nome: empresa?.nome || '',
      nomeFantasia: empresa?.nomeFantasia || '',
      cnpj: empresa?.cnpj || '',
      tipoUnidade: empresa?.tipo
        ? {
            value: empresa.tipo.id,
            label: empresa.tipo.nome || empresa.tipo.descricao || '',
          }
        : undefined,
      responsavel: {
        cpf: rt?.pessoa?.cpf || '',
        primeiroNome: rt?.pessoa?.primeiroNome || '',
        sobreNome: rt?.pessoa?.sobreNome || '',
        email: rt?.pessoa?.usuario?.email || '',
        telefone: rt?.pessoa?.telefone || '',
        nascimento: rt?.pessoa?.dataNascimento || '',
        sexo: rt?.pessoa?.sexo
          ? {
              label: rt.pessoa.sexo === GenderType.Feminino ? 'Feminino' : 'Masculino',
              value: rt.pessoa.sexo,
            }
          : undefined,
        conselho: rt?.conselho
          ? {
              value: rt.conselho.id,
              label: `${rt.conselho.sigla ?? ''} ${rt.conselho.nome ?? ''}`.trim(),
            }
          : undefined,
        crm: rt?.crm || '',
        uf: rt?.uf ? { value: rt.uf, label: rt.uf } : undefined,
        rqe: rt?.rqe || '',
      },
      logo: logo ? convertToAvatarDto(logo, 'u') : undefined,
      tiposAtendimento:
        OptionsHelper.createOptions(empresa?.tiposAtendimentoMap || [], 'descricao', 'name') || [],
      permiteNovoPaciente:
        empresa?.permiteNovoPaciente !== undefined ? empresa.permiteNovoPaciente : true,
      tiposContrato:
        OptionsHelper.createOptions(empresa?.tiposContratoMap || [], 'descricao', 'name') || [],
      email: contato?.email || '',
      telefone: contato?.telefoneContato || '',
      cnes: empresa?.cnes || '',
      codigoSes: empresa?.codigoSes || '',
      nomeCnes: empresa?.nomeCnes || '',
      sus: !!empresa?.sus,
      limiteUnidades: empresa?.limiteUnidades || null,
      inicioContrato: empresa?.inicioContrato || '',
      terminoContrato: empresa?.terminoContrato || '',

      grupoTelegram: !!empresa?.grupoTelegram ? empresa?.grupoTelegram : '',

      apiWebhook: empresa?.apiWebhook || '',
      dominiosIntegracao: empresa?.dominiosIntegracao || [],

      empresaMae: empresa
        ? { label: empresa.nomeFantasia, value: empresa.id }
        : (undefined as IOption<number> | undefined),
      especialidades:
        empresa?.especialidades?.map((esp) => ({
          label: esp.especialidade.nome,
          value: esp.especialidade.id,
          tempoConsulta: esp.tempoConsulta,
          grupoTelegram: esp.grupoTelegram || '',
          gruposTelegram: Object.entries(esp.gruposTelegram || {}).map(
            ([tipoAtendimentoKey, grupoTelegram]) => ({
              tipoAtendimento: getTipoAtendimentoOption(tipoAtendimentoKey as AtendimentoType),
              grupoTelegram,
            })
          ),
        })) ?? [],
      observacoes: empresa?.observacoes || [],
      diaVencimentoFatura: empresa?.faturamento?.diaVencimentoFatura,
      faturamentoMinimo: empresa?.faturamento?.faturamentoMinimo,
      valorAtendimento: empresa?.faturamento?.valorAtendimento,
      valorRetorno: empresa?.faturamento?.valorRetorno,
      faturamentoEspecialidade: !!empresa?.faturamento?.faturamentoEspecialidade,
      faturamentoUnidade: !!empresa?.faturamento?.faturamentoUnidade,
      slug: empresa?.slug || '',
      imagem: undefined as AvatarDto | undefined,
      hilab: criarHilabFormValues(empresa?.hilab),
      exige2fa: !!empresa?.exige2fa,
    }),
    [empresa, rt, logo, contato, getTipoAtendimentoOption]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: empresaForEdit,
    validationSchema: useMemo(() => createEmpresaSchema(t, mode), [t, mode]),
    onSubmit: async (values, { setSubmitting }) => {
      const tiposAtendimento = values.tiposAtendimento.map(
        (option) => option.value
      ) as AtendimentoType[]
      const tiposContrato = values.tiposContrato.map((option) => option.value) as ContratoType[]
      const hilab = criarEmpresaDtoHilab(values.hilab)
      const especialidades = values.especialidades.map((item) => ({
        id: Number(item.value),
        tempoConsulta: item.tempoConsulta ? Number(item.tempoConsulta) : undefined,
        grupoTelegram: item.grupoTelegram,
        gruposTelegram:
          item.gruposTelegram && item.gruposTelegram.length > 0
            ? item.gruposTelegram.map((item) => ({
                tipo: item.tipoAtendimento?.value as AtendimentoType,
                grupoTelegram: item.grupoTelegram,
              }))
            : [],
      }))

      const tipo = values.tipoUnidade?.value ? Number(values.tipoUnidade.value) : undefined

      let responsavelPayload: ResponsavelTecnicoDto | undefined = undefined
      if (values.responsavel?.cpf && values.responsavel.cpf.trim().length > 0) {
        responsavelPayload = {
          cpf: values.responsavel.cpf,
          primeiroNome: values.responsavel.primeiroNome || undefined,
          sobreNome: values.responsavel.sobreNome || undefined,
          email: values.responsavel.email || undefined,
          telefone: values.responsavel.telefone || undefined,
          sexo: (values.responsavel.sexo?.value as GenderType) || undefined,
          nascimento: values.responsavel.nascimento || undefined,
          idConselho: values.responsavel.conselho?.value
            ? Number(values.responsavel.conselho.value)
            : undefined,
          crm: values.responsavel.crm || undefined,
          uf:
            typeof values.responsavel.uf === 'object' && values.responsavel.uf?.value
              ? String(values.responsavel.uf.value)
              : typeof values.responsavel.uf === 'string'
              ? values.responsavel.uf
              : undefined,
          rqe: values.responsavel.rqe || undefined,
        }
      }

      setSubmitting(true)
      try {
        if (values.id) {
          const empresaUpdate: EmpresaDtoUpdate = {
            id: values.id,
            nome: values.nome,
            nomeFantasia: values.nomeFantasia,
            cnpj: values.cnpj,
            tipo,
            responsavelTecnico: responsavelPayload,
            apiWebhook: values.apiWebhook,
            dominiosIntegracao: values.dominiosIntegracao,
            grupoTelegram: values.grupoTelegram,
            permiteNovoPaciente: values.permiteNovoPaciente,
            ativa: values.ativa,
            tiposAtendimento,
            tiposContrato,
            emailContato: values.email,
            telefoneContato: values.telefone ? values.telefone : undefined,
            logo: values.logo
              ? { ...values.logo, fileName: `logo-${values.nome}-${values.nomeFantasia}` }
              : undefined,
            imagem: values.imagem
              ? { ...values.imagem, fileName: `imagem-login-${values.nome}-${values.nomeFantasia}` }
              : undefined,
            slug: values.slug,
            cnes: values.cnes,
            nomeCnes: values.nomeCnes,
            codigoSes: values.codigoSes || undefined,
            sus: values.sus,
            especialidades,
            limiteUnidades: normalizeNumber(values.limiteUnidades),
            terminoContrato: values.terminoContrato || undefined,
            inicioContrato: values.inicioContrato || undefined,
            observacoes: values.observacoes,
            diaVencimentoFatura: values.diaVencimentoFatura,
            faturamentoMinimo: values.faturamentoMinimo || null,
            valorAtendimento: values.valorAtendimento,
            valorRetorno: values.valorRetorno,
            faturamentoEspecialidade: values.faturamentoEspecialidade || null,
            faturamentoUnidade: values.faturamentoUnidade || null,
            hilab,
            exige2fa: empresa?.superior ? undefined : values.exige2fa,
          }
          await updateEmpresa(empresaUpdate)
        } else {
          const empresaCreate: EmpresaDtoCreate = {
            nome: values.nome,
            nomeFantasia: values.nomeFantasia,
            cnpj: values.cnpj,
            tipo,
            responsavelTecnico: responsavelPayload,
            apiWebhook: values.apiWebhook,
            dominiosIntegracao: values.dominiosIntegracao,
            grupoTelegram: values.grupoTelegram,
            permiteNovoPaciente: values.permiteNovoPaciente,
            tiposAtendimento,
            tiposContrato,
            emailContato: values.email,
            telefoneContato: values.telefone ? values.telefone : undefined,
            logo: values.logo
              ? { ...values.logo, fileName: `logo-${values.nome}-${values.nomeFantasia}` }
              : undefined,
            imagem: values.imagem
              ? { ...values.imagem, fileName: `imagem-login-${values.nome}-${values.nomeFantasia}` }
              : undefined,
            slug: values.slug,
            cnes: values.cnes,
            nomeCnes: values.nomeCnes,
            codigoSes: values.codigoSes || undefined,
            sus: values.sus,
            especialidades,
            limiteUnidades: normalizeNumber(values.limiteUnidades),
            inicioContrato: values.inicioContrato || undefined,
            observacoes: values.observacoes,
            diaVencimentoFatura: values.diaVencimentoFatura,
            faturamentoMinimo: values.faturamentoMinimo || null,
            valorAtendimento: values.valorAtendimento,
            valorRetorno: values.valorRetorno,
            faturamentoEspecialidade: values.faturamentoEspecialidade || null,
            faturamentoUnidade: values.faturamentoUnidade || null,
            hilab,
          }

          if (mode === 'cliente') {
            await createSubEmpresa(Number(values.empresaMae?.value), empresaCreate)
          } else {
            await createEmpresa(empresaCreate)
          }
        }

        await showAlert({
          title: 'Sucesso!',
          text: `${t('empresa.label')} ${values.id ? 'atualizada' : 'cadastrada'} com sucesso!`,
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        const temOutroErro = applyApiFieldErrors(error, formik.setFieldError, [
          'cnpj',
          'cnes',
          'codigoSes',
        ])
        if (temOutroErro) showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const responsavelCpf = formik.values.responsavel?.cpf
  const responsavelObrigatorio = !!responsavelCpf && responsavelCpf.trim().length > 0
  const { setValues } = formik
  useEffect(() => {
    if (responsavelCpf && responsavelCpf.replace(/\D/g, '').length === 11) {
      getUserByCPF(responsavelCpf)
        .then((resp) => {
          if (resp.data) {
            const user = resp.data
            // Nome, telefone, sexo e nascimento vêm em `pessoa` (UsuarioDtoVinculo); só o e-mail fica na raiz
            const pessoa = user.pessoa
            // Forma funcional: lê o formulário atual na hora de preencher, sem depender de `formik`
            // (que muda a cada render) — só completa o que o usuário ainda não digitou.
            setValues((atual) => {
              const responsavel = { ...atual.responsavel }
              if (!responsavel.primeiroNome && pessoa?.primeiroNome)
                responsavel.primeiroNome = pessoa.primeiroNome
              if (!responsavel.sobreNome && pessoa?.sobreNome)
                responsavel.sobreNome = pessoa.sobreNome
              if (!responsavel.email && user.email) responsavel.email = user.email
              if (!responsavel.telefone && pessoa?.telefone) responsavel.telefone = pessoa.telefone
              if (!responsavel.nascimento && pessoa?.dataNascimento)
                responsavel.nascimento = pessoa.dataNascimento
              if (!responsavel.sexo && pessoa?.sexo)
                responsavel.sexo = OptionsHelper.generos.find((g) => g.value === pessoa.sexo)
              return { ...atual, responsavel }
            })
          }
        })
        .catch(() => {})
    }
  }, [responsavelCpf, setValues])

  const handleDeleteObservacoes = (id: number) => {
    const novas = formik.values.observacoes.filter((_, i) => i !== id)
    formik.setFieldValue('observacoes', novas)
  }

  const handleDeleteDominioIntegracao = (id: number) => {
    const novos = formik.values.dominiosIntegracao.filter((_, i) => i !== id)
    formik.setFieldValue('dominiosIntegracao', novos)
  }

  const extraParams = useMemo(() => ({ onlySuperior: true }), [])
  const extraParamsContratos = useMemo(
    () => ({ excludeContrato: mode === 'cliente' ? ContratoType.Layout : undefined }),
    [mode]
  )

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {mode === 'cliente' && (
        <InfoAlert title='Atenção' variant='danger'>
          Se criar uma {t('entidade.label')}, você estará separando completamente os prontuários.
          Para manter um histórico único, utilize uma Unidade/Setor da sua{' '}
          {t('empresa.label.lowercase')}.
        </InfoAlert>
      )}
      {empresaMaeObrigatoria && !empresa && (
        <FormSelect
          formik={formik}
          name='empresaMae'
          entity='empresa_responsavel'
          fetchOptions={OptionsHelper.getOptionsEmpresasAdmin}
          fetchOptionsParams={extraParams}
          className='mb-3'
          isDisabled={formik.isSubmitting}
          isRequired
        />
      )}
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tiposAtendimento'
            entity='tipoAtendimento'
            fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
            isDisabled={formik.isSubmitting}
            isRequired
            isMulti
          />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tiposContrato'
            entity='contrato'
            fetchOptions={OptionsHelper.getOptionsTiposContrato}
            fetchOptionsParams={extraParamsContratos}
            isDisabled={formik.isSubmitting}
            isRequired
            isMulti
          />
        </div>
      </div>
      <FormSelect
        formik={formik}
        name='especialidades'
        entity='especialidade'
        placeholder={t('especialidade.info')}
        infoText='Se não for informado, todas as especialidades estarão disponíveis.'
        fetchOptions={OptionsHelper.getOptionsEspecialidades}
        isDisabled={formik.isSubmitting}
        className='mb-3'
        isMulti
        highlight
      />
      {formik.values.especialidades && (
        <CustomAccordion
          title={`Gerenciar especialidades (${formik.values.especialidades.length})`}
        >
          <div className='d-flex flex-column gap-4'>
            {formik.values.especialidades.map((item, index) => (
              <div key={String(item.value)} className='card border border-light'>
                <div className='card-body p-4'>
                  <div className='d-flex justify-content-between align-items-center mb-3'>
                    <div className='fw-semibold'>{item.label}</div>
                  </div>

                  <div className='row g-3'>
                    <div className='col-md-3 mb-3'>
                      <FormInput
                        formik={formik}
                        name={`especialidades[${index}].tempoConsulta`}
                        type='number'
                        entity='duracao'
                        placeholder='Min'
                      />
                    </div>

                    <div className='col-md-9'>
                      <FormInput
                        formik={formik}
                        name={`especialidades[${index}].grupoTelegram`}
                        entity='grupoTelegram_padrao'
                      />
                    </div>
                  </div>

                  {(item.gruposTelegram?.length ?? 0) === 0 && (
                    <div className='text-muted fs-7 mb-3'>
                      Nenhum grupo do telegram por tipo de atendimento cadastrado
                    </div>
                  )}

                  {(item.gruposTelegram || []).map((_, grupoIndex) => (
                    <div key={grupoIndex} className='row g-3 mb-3 align-items-center'>
                      <div className='col-md-4'>
                        <FormSelect
                          formik={formik}
                          name={`especialidades[${index}].gruposTelegram[${grupoIndex}].tipoAtendimento`}
                          entity='tipoAtendimento'
                          fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
                          isDisabled={formik.isSubmitting}
                        />
                      </div>

                      <div className='col-md-7'>
                        <FormInput
                          formik={formik}
                          name={`especialidades[${index}].gruposTelegram[${grupoIndex}].grupoTelegram`}
                          entity='grupoTelegram'
                          isDisabled={formik.isSubmitting}
                        />
                      </div>

                      <div className='d-flex  justify-content-end col-md-1'>
                        <ButtonIcon
                          icon={IconPaths.Trash}
                          iconSize={IconSize.Small}
                          className='p-0 m-0'
                          onClick={() => {
                            const grupos = item.gruposTelegram || []
                            formik.setFieldValue(
                              `especialidades[${index}].gruposTelegram`,
                              grupos.filter((_, i) => i !== grupoIndex)
                            )
                          }}
                        />
                      </div>
                    </div>
                  ))}
                  <div className='d-flex flex-end'>
                    <Button
                      className='btn-primary'
                      size='btn-sm'
                      icon={IconPaths.Add}
                      onClick={() => {
                        const grupos = formik.values.especialidades[index].gruposTelegram || []
                        formik.setFieldValue(`especialidades[${index}].gruposTelegram`, [
                          ...grupos,
                          { tipoAtendimento: undefined, grupoTelegram: '' },
                        ])
                      }}
                    >
                      {t('common.add')}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CustomAccordion>
      )}

      <FormToggle
        className='mb-3'
        formik={formik}
        name='permiteNovoPaciente'
        entity='permite_novos_pacientes'
        isDisabled={formik.isSubmitting}
      />
      <FormToggle
        formik={formik}
        name='faturamentoEspecialidade'
        entity='faturamento_especialidade'
        className='mb-3'
        isDisabled={formik.isSubmitting}
      />
      <FormToggle
        formik={formik}
        name='faturamentoUnidade'
        entity='faturamento_unidade'
        className='mb-3'
        isDisabled={formik.isSubmitting}
      />
      {formik.values.id && !empresa?.superior && (
        <FormToggle
          formik={formik}
          name='exige2fa'
          label='Exigir verificação em duas etapas'
          infoText='Todos os usuários vinculados a esta empresa passarão a precisar de um código do aplicativo autenticador além da senha. Quem já ativou a verificação por conta própria deixa de poder desativá-la.'
          className='mb-3'
        />
      )}
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput formik={formik} name='nome' entity='nome' isRequired />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='nomeFantasia' entity='nome_fantasia' isRequired />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput formik={formik} name='cnpj' entity='cnpj' mask={MaskType.CNPJ} isRequired />
        </div>
        <div className='col-md-6'>
          <FormSelect
            formik={formik}
            name='tipoUnidade'
            label='Tipo de Estabelecimento'
            fetchOptions={OptionsHelper.getOptionsTiposUnidades}
            placeholder='Selecione o tipo de estabelecimento'
            isRequired
            isDisabled={formik.isSubmitting}
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <Label className='mb-2'>Logo da {t('empresa.label')}</Label>
          <LogoInput
            onChange={(value) => formik.setFieldValue('logo', value)}
            value={logo}
            disabled={formik.isSubmitting}
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='email' entity='email' />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='telefone'
            entity='telefone'
            mask={MaskType.Phone}
            isRequired={formik.values.tiposContrato.some(
              (contrato) =>
                contrato.value === ContratoType.Link_Celular ||
                contrato.value === ContratoType.Link_Plataforma
            )}
          />
        </div>
        <div className='col-md-6'>
          <FormInput formik={formik} name='grupoTelegram' entity='grupoTelegram' />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            type='number'
            name='diaVencimentoFatura'
            placeholder='Digite o dia de vencimento da fatura (dia)'
            label='Dia do vencimento da fatura'
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            type='number'
            name='faturamentoMinimo'
            placeholder='Quantidade mínima de atendimentos mensais'
            label='Mínimo de atendimentos'
            infoText='Caso deixado em branco a empresa não terá quantidade mínima'
            highlight
          />
        </div>
        <div className='col-md-3'>
          <FormInput
            formik={formik}
            type='number'
            name='valorAtendimento'
            placeholder='Digite o valor do atendimento'
            label='Valor padrão do atendimento'
            infoText='Valor padrão caso não seja configurado em gerenciar valores da empresa'
            highlight
          />
        </div>
        <div className='col-md-3'>
          <FormInput
            formik={formik}
            type='number'
            name='valorRetorno'
            placeholder='Digite o valor do retorno'
            label='Valor padrão do retorno'
            infoText='Valor padrão caso não seja configurado em gerenciar valores da empresa'
            highlight
          />
        </div>
      </div>

      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormRadio<boolean>
            formik={formik}
            name='sus'
            label='Atendimento realizado pelo SUS?'
            isDisabled={formik.isSubmitting}
            isRequired
            options={[
              { id: 1, descricao: 'Sim', value: true },
              { id: 2, descricao: 'Não', value: false },
            ]}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            type='number'
            name='limiteUnidades'
            placeholder='Digite o limite de unidades'
            label='Limite de Unidades'
            infoText='Deixe vazio para ilimitado, 0 para desabilitar o cadastro ou escolha a quantidade limite'
            highlight
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='nomeCnes'
            entity='nome_cnes'
            infoText={t('cnes.infoText')}
          />
        </div>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='cnes'
            entity='cnes'
            mask={MaskType.CNES}
            infoText={t('cnes.infoText')}
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormInput
            formik={formik}
            name='codigoSes'
            label='Código do Núcleo'
            mask={MaskType.CNES}
            infoText='Código de 7 dígitos fornecido pela Secretaria de Saúde. Preenchido, a empresa passa a ser Núcleo credenciado e a abertura de interconsultas exige o cadastro completo para o RATC (CNS do paciente, CNES da empresa, lotação do solicitante com CBO, CNES e código IBGE da unidade).'
          />
        </div>
      </div>
      <div className='row mb-3'>
        <div className='col-md-6'>
          <FormDateInput
            formik={formik}
            name='inicioContrato'
            entity='inicio_contrato'
            maxDate={formik.values.terminoContrato}
          />
        </div>
        {!!empresa && (
          <div className='col-md-6'>
            <FormDateInput
              formik={formik}
              name='terminoContrato'
              entity='termino_contrato'
              minDate={formik.values.inicioContrato}
            />
          </div>
        )}
      </div>
      {formik.values.tiposContrato.some((item) => item.value === ContratoType.Layout) && (
        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='slug'
              placeholder='Digite o identificador da empresa'
              label='Identificador da empresa'
              infoText='Será usado para acessar o sistema. Exemplo: /auth/Identificador da empresa/login'
            />
          </div>
          <div className='col-md-6'>
            <Label className='mb-2'>Imagem de login da empresa</Label>
            <LogoInput
              onChange={(value) => formik.setFieldValue('imagem', value)}
              disabled={formik.isSubmitting}
            />
          </div>
        </div>
      )}

      <CustomAccordion
        title='Responsável Técnico'
        defaultExpanded={!!formik.values.responsavel?.cpf}
      >
        <div className='row my-2'>
          <h2>Dados do Responsável Técnico</h2>
        </div>
        <div className='row mb-3'>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='responsavel.cpf'
              label='CPF'
              placeholder='000.000.000-00'
              mask={MaskType.CPF}
              isDisabled={possuiResponsavelProprio}
              infoText={
                possuiResponsavelProprio
                  ? 'Para trocar o responsável técnico, acesse o cadastro de funcionários.'
                  : ''
              }
              highlight
            />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='responsavel.primeiroNome'
              label='Nome'
              placeholder='Nome do profissional'
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-4'>
            <FormInput
              formik={formik}
              name='responsavel.sobreNome'
              label='Sobrenome'
              placeholder='Sobrenome do profissional'
              isRequired={responsavelObrigatorio}
            />
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='responsavel.email'
              label='E-mail'
              placeholder='email@exemplo.com'
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-6'>
            <FormInput
              formik={formik}
              name='responsavel.telefone'
              label='Telefone'
              mask={MaskType.Phone}
              placeholder='(00) 0 0000-0000'
            />
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-6'>
            <FormDateInput
              formik={formik}
              name='responsavel.nascimento'
              label='Data de nascimento'
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-6'>
            <FormSelect
              formik={formik}
              name='responsavel.sexo'
              label='Sexo'
              options={OptionsHelper.generos}
              placeholder='Selecione o sexo'
              isDisabled={formik.isSubmitting}
              isRequired={responsavelObrigatorio}
            />
          </div>
        </div>

        <div className='row mb-3'>
          <div className='col-md-3'>
            <FormSelect
              formik={formik}
              name='responsavel.conselho'
              label='Conselho'
              fetchOptions={OptionsHelper.getOptionsConselhos}
              placeholder='Selecione o conselho'
              isDisabled={formik.isSubmitting}
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-3'>
            <FormInput
              formik={formik}
              name='responsavel.crm'
              label='Registro no Conselho'
              placeholder='Número do registro'
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-3'>
            <FormSelect
              formik={formik}
              name='responsavel.uf'
              label='UF do Conselho'
              fetchOptions={OptionsHelper.getOptionsEstadosUf}
              placeholder='UF'
              isDisabled={formik.isSubmitting}
              isRequired={responsavelObrigatorio}
            />
          </div>
          <div className='col-md-3'>
            <FormInput
              formik={formik}
              name='responsavel.rqe'
              label='RQE'
              placeholder='Registro de qualificação'
              infoText='Registro de Qualificação de Especialista: número emitido pelo conselho para profissionais com especialidade reconhecida. Preencha apenas se o responsável possuir.'
              highlight
            />
          </div>
        </div>
      </CustomAccordion>

      <CustomAccordion title='Observações'>
        <div className='row my-2'>
          <h2>Observações</h2>
        </div>
        {formik.values.observacoes.length === 0 && (
          <div className='text-muted fs-7 mb-3'>Nenhuma observação cadastrada</div>
        )}
        {formik.values.observacoes.map((_, index) => (
          <div key={`${index}_observacoes`} className='d-flex align-items-center gap-2 mb-3'>
            <FormTextArea
              formik={formik}
              name={`observacoes.${index}`}
              placeholder='Digite a observação'
              isDisabled={formik.isSubmitting}
              className='w-100'
            />
            <ButtonIcon
              icon={IconPaths.Trash}
              iconSize={IconSize.Small}
              onClick={() => handleDeleteObservacoes(index)}
              className='p-0'
            />
          </div>
        ))}
        <div className='d-flex flex-end'>
          <Button
            className='btn-primary'
            size='btn-sm'
            icon={IconPaths.Add}
            onClick={() => formik.setFieldValue('observacoes', [...formik.values.observacoes, ''])}
          >
            {t('common.add')}
          </Button>
        </div>
      </CustomAccordion>

      <CustomAccordion title='Integração via API'>
        <div className='row my-2'>
          <h2>Integração via API</h2>
        </div>
        <FormInput
          formik={formik}
          name='apiWebhook'
          label='URL do WebHook'
          placeholder='Insira a URL do WebHook'
          className='mb-3'
          isDisabled={formik.isSubmitting}
        />

        <Label
          className='mb-2'
          infoText='Domínios autorizados a chamar /integracao/** via browser (CORS). Informe só o domínio (ex: cliente.com.br) — já libera automaticamente qualquer subdomínio.'
        >
          Domínios de integração
        </Label>
        {formik.values.dominiosIntegracao.length === 0 && (
          <div className='text-muted fs-7 mb-3'>Nenhum domínio de integração cadastrado</div>
        )}
        {formik.values.dominiosIntegracao.map((_, index) => (
          <div key={`${index}_dominiosIntegracao`} className='d-flex align-items-center gap-2 mb-3'>
            <FormInput
              formik={formik}
              name={`dominiosIntegracao.${index}`}
              placeholder='Digite o domínio (ex: cliente.com.br)'
              isDisabled={formik.isSubmitting}
              className='w-100'
            />
            <ButtonIcon
              icon={IconPaths.Trash}
              iconSize={IconSize.Small}
              onClick={() => handleDeleteDominioIntegracao(index)}
              className='p-0'
            />
          </div>
        ))}
        <div className='d-flex flex-end'>
          <Button
            className='btn-primary'
            size='btn-sm'
            icon={IconPaths.Add}
            onClick={() =>
              formik.setFieldValue('dominiosIntegracao', [...formik.values.dominiosIntegracao, ''])
            }
          >
            {t('common.add')}
          </Button>
        </div>
      </CustomAccordion>

      <CustomAccordion title='Integração Hilab'>
        <div className='d-flex flex-column flex-md-row justify-content-between gap-3 mb-4'>
          <div>
            <h2 className='mb-1'>Integração Hilab</h2>
            <div className='text-muted fs-7'>
              Configurações necessárias para uso pela LauduzOne dos equipamentos Hilab.
            </div>
          </div>
          <FormToggle
            formik={formik}
            name='hilab.ativo'
            label='Integração ativa'
            isDisabled={formik.isSubmitting}
          />
        </div>
        <div className='row g-4'>
          <div className='col-md-6 mb-3'>
            <FormInput
              formik={formik}
              name='hilab.oauthUser'
              label='Usuário OAuth'
              placeholder='Informe o usuário da integração'
              isRequired={!!formik.values.hilab.ativo}
            />
          </div>
          <div className='col-md-6 mb-3'>
            <FormInput
              formik={formik}
              name='hilab.oauthPassword'
              label='Senha OAuth'
              placeholder={
                formik.values.hilab.senhaCadastrada
                  ? 'Deixe vazio para manter a senha atual'
                  : 'Informe a senha da integração'
              }
              type='password'
              autoComplete='new-password'
              isRequired={!!formik.values.hilab.ativo && !formik.values.hilab.senhaCadastrada}
            />
            {formik.values.hilab.senhaCadastrada && (
              <span className='text-success fs-7' role='status'>
                Senha já cadastrada. Informe outra somente para substituí-la.
              </span>
            )}
          </div>
        </div>
        <div className='row g-4'>
          <div className='col-md-6 mb-3'>
            <FormInput
              formik={formik}
              name='hilab.webhookHeaderName'
              label='Nome do cabeçalho do webhook'
              placeholder='Ex.: X-Hilab-Signature'
            />
          </div>
          <div className='col-md-6 mb-3'>
            <FormInput
              formik={formik}
              name='hilab.webhookSecret'
              label='Segredo do webhook'
              placeholder={
                formik.values.hilab.segredoCadastrado
                  ? 'Deixe vazio para manter o segredo atual'
                  : 'Informe o segredo do webhook'
              }
              type='password'
              autoComplete='new-password'
            />
            {formik.values.hilab.segredoCadastrado && (
              <span className='text-success fs-7' role='status'>
                Segredo já cadastrado. Informe outro somente para substituí-lo.
              </span>
            )}
          </div>
        </div>
      </CustomAccordion>

      <InfoAlert title='Endereços' variant='info' className='mt-8'>
        Os endereços não são mais cadastrados aqui. Use a ação <strong>Listar endereços</strong> no
        menu desta {t('empresa.label.lowercase')} para cadastrar e manter os endereços — inclusive o
        Comercial, que é o usado nos documentos emitidos (prescrição, laudo e relatórios).
      </InfoAlert>

      <div className='d-flex justify-content-center mt-8'>
        <Button
          type={ButtonType.Reset}
          onClick={closeModal}
          className='btn-secondary me-3'
          disabled={formik.isSubmitting || isEmpresaLoading}
        >
          {t('common.cancel')}
        </Button>
        <Button
          type={ButtonType.Submit}
          className='btn-primary'
          disabled={formik.isSubmitting || isEmpresaLoading}
        >
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
      {(formik.isSubmitting || isEmpresaLoading) && <ListLoading />}
    </form>
  )
}
